/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { RequestOptions } from 'mixpanel-browser'

import { ZUMPER_SITE_TYPES } from '../enums'
import { logger } from '../helpers/logger'
import { Payload } from '../types'

import { EVENT_NAMES, PROP_NAMES } from '../constants'

interface Props {
  event: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
  payload?: Payload
  zSite?: ZUMPER_SITE_TYPES
  options?: RequestOptions
  callback?: () => void
}

const EVENT_NAME_VALUES = new Set(Object.values(EVENT_NAMES))
const PROP_NAME_VALUES = new Set(Object.values(PROP_NAMES))

export const track = ({
  event,
  payload = {},
  zSite = ZUMPER_SITE_TYPES.CONSUMER,
  options = undefined,
  callback = undefined,
}: Props) => {
  let shouldTrackEvent = true
  // Copying so modifications can be made
  payload = { ...payload }
  try {
    if (!EVENT_NAME_VALUES.has(event)) {
      logger.error(`Mixpanel.track: Can't track unexpected event ${event}`)
      shouldTrackEvent = false
    }
    for (const key of Object.keys(payload)) {
      if (!PROP_NAME_VALUES.has(key as keyof Payload)) {
        logger.error(`Mixpanel.track: Got unexpected prop ${key}`)
        shouldTrackEvent = false
      }
    }
    payload[PROP_NAMES.ZUMPER_SITE] = zSite
    payload[PROP_NAMES.HOST] = window.location.hostname
    if (shouldTrackEvent) {
      if (window?.dataLayer) {
        const gtmPayload = {
          event: 'mixpanelEventSimplified',
          mixpanelEvent: event,
          mixpanelPayload: payload,
          mixpanelOptions: options,
        }
        logger.debug('Tracking Mixpanel Event gtm:', event, gtmPayload)
        // @ts-expect-error
        window.dataLayer.push(gtmPayload) // <-- does not match type
        if (typeof callback === 'function') {
          callback() // callback doesn't work when GTM is called from partytown, so calling it manually here
        }
      } else if (window?.mixpanel) {
        logger.debug(
          'Tracking Mixpanel Event mixpanel:',
          event,
          payload,
          options
        )
        window.mixpanel.track(event, payload, options, callback)
      } else {
        logger.warn(
          'Could not send mixpanel event. DataLayer and Mixpanel objects have not been initialized'
        )
      }
    }
  } catch (e: any) {
    logger.error('Tracking Mixpanel error:', e.message, event, payload)
  }
}
